import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import LaptopChromebookRoundedIcon from '@mui/icons-material/LaptopChromebookRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import Section, { SectionHeading } from './PageSection';
import {
  FeatureCard,
  FeatureIcon,
  CardTitle,
  CardText,
  accentAt,
  AccreditationNote,
} from './ui';
import { fetchNewsIndex, CategoryChip, NewsThumb, formatNewsDate } from '../news';

// Homepage sections mirroring the original qasc.it home: i servizi, i corsi in
// evidenza, la consulenza, gli accreditamenti e le ultime news. Deep copy
// lives on the dedicated pages, not here.

const SectionLink = ({ to, children, dark }) => (
  <Box sx={{ textAlign: 'center', mt: 5 }}>
    <Typography
      component={RouterLink}
      to={to}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        fontFamily: '"Sora", sans-serif',
        fontWeight: 600,
        fontSize: '15.5px',
        color: dark ? '#8fc1d6' : 'primary.main',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {children}
      <ArrowForwardRoundedIcon sx={{ fontSize: '20px' }} />
    </Typography>
  </Box>
);

// ------------------------------------------------------------------- Servizi
// The original home lists three services: Formazione, Consulenza, E-Learning.
export function ServicesSection() {
  const services = [
    {
      icon: <SchoolRoundedIcon />,
      title: 'Formazione',
      text: 'Corsi sulla sicurezza sul lavoro progettati secondo il D.Lgs. 81/08 e gli Accordi Stato-Regioni, corsi di ambiente e qualità e formazione finanziata.',
      to: '/formazione',
      link: 'Scopri di più',
    },
    {
      icon: <HandshakeRoundedIcon />,
      title: 'Consulenza',
      text: 'Sistemi di gestione, valutazione del rischio, modello organizzativo 231 e sicurezza alimentare: un supporto concreto e diretto alle imprese.',
      to: '/consulenza',
      link: 'Scopri di più',
    },
    {
      icon: <LaptopChromebookRoundedIcon />,
      title: 'E-Learning',
      text: 'Formazione per aziende online: percorsi fruibili in ogni momento da PC o smartphone, con report e attestato via e-mail a fine corso.',
      to: '/e-learning',
      link: 'Scopri di più',
    },
  ];
  return (
    <Section tone="light">
      <Container maxWidth="lg">
        <SectionHeading
          kicker="I servizi di QAS Consulting"
          title="La vostra fiducia è il nostro più grande successo"
        />
        <Grid container spacing={4}>
          {services.map((s, i) => (
            <Grid item xs={12} md={4} key={s.title}>
              <FeatureCard
                component={RouterLink}
                to={s.to}
                sx={{ display: 'flex', flexDirection: 'column', textDecoration: 'none' }}
              >
                <FeatureIcon accent={accentAt(i)}>{s.icon}</FeatureIcon>
                <CardTitle>{s.title}</CardTitle>
                <CardText sx={{ flexGrow: 1 }}>{s.text}</CardText>
                <Typography
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    mt: 2.5,
                    color: 'primary.main',
                    fontFamily: '"Sora", sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    '& svg': { fontSize: '17px' },
                  }}
                >
                  {s.link}
                  <ArrowForwardRoundedIcon />
                </Typography>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Section>
  );
}

// ---------------------------------------------------------- Corsi in evidenza
// The three featured courses of the original home, with their real summaries.
export function FeaturedCoursesSection() {
  const featured = [
    {
      title: 'Formazione generale dei lavoratori',
      text: 'La formazione dei lavoratori sulla sicurezza è uno dei principali obblighi a carico del Datore di Lavoro, così come previsto in diversi articoli del D.Lgs. 81/08 — primo fra tutti l’art. 37, che ne definisce contenuti e durata secondo l’Accordo Stato-Regioni.',
    },
    {
      title: 'Formazione specifica — rischio basso, medio e alto',
      text: 'L’approfondimento della formazione in funzione del settore di appartenenza dell’azienda, secondo la classificazione basata sui codici ATECO: rischio basso, medio o alto. In alcuni casi il Datore di Lavoro può svolgere direttamente il ruolo di RSPP (art. 34 D.Lgs. 81/08).',
    },
    {
      title: 'Formazione RLS',
      text: 'Il Rappresentante dei lavoratori per la sicurezza è la “persona eletta o designata per rappresentare i lavoratori per quanto concerne gli aspetti della salute e della sicurezza durante il lavoro”, un punto di riferimento per tutti i lavoratori.',
    },
  ];
  return (
    <Section tone="mist">
      <Container maxWidth="lg">
        <SectionHeading
          kicker="Corsi in evidenza"
          title="I corsi in evidenza"
          subtitle="I percorsi che rispondono agli obblighi formativi più diffusi a carico del Datore di Lavoro."
        />
        <Grid container spacing={4}>
          {featured.map((c, i) => (
            <Grid item xs={12} md={4} key={c.title}>
              <FeatureCard>
                <FeatureIcon accent={accentAt(i)}>
                  <SchoolRoundedIcon />
                </FeatureIcon>
                <CardTitle>{c.title}</CardTitle>
                <CardText>{c.text}</CardText>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
        <SectionLink to="/corsi-in-evidenza">Tutti i corsi in evidenza</SectionLink>
      </Container>
    </Section>
  );
}

// ---------------------------------------------------------------------- News
// The three latest articles from the migrated archive (public/data/news/).
export function NewsSection() {
  const [news, setNews] = React.useState([]);
  React.useEffect(() => {
    let alive = true;
    fetchNewsIndex()
      .then((all) => alive && setNews(all.slice(0, 3)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  if (!news.length) return null;
  return (
    <Section tone="mist">
      <Container maxWidth="lg">
        <SectionHeading
          kicker="News & Aggiornamenti"
          title="News e pillole di sicurezza"
          subtitle="Gli ultimi aggiornamenti normativi su sicurezza, ambiente e qualità."
        />
        <Grid container spacing={4}>
          {news.map((n) => (
            <Grid item xs={12} md={4} key={n.slug}>
              <FeatureCard
                component={RouterLink}
                to={`/news/${n.slug}`}
                sx={{ p: 0, display: 'flex', flexDirection: 'column', textDecoration: 'none', overflow: 'hidden' }}
              >
                <NewsThumb image={n.image} title={n.title} />
                <Box sx={{ p: '22px 24px 26px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '13px', color: '#7a8f9a' }}>
                      {formatNewsDate(n.date)}
                    </Typography>
                    {(n.categories || []).slice(0, 1).map((c) => (
                      <CategoryChip key={c} label={c} />
                    ))}
                  </Box>
                  <CardTitle sx={{ fontSize: '16px', lineHeight: 1.5, mb: 1 }}>{n.title}</CardTitle>
                  <Typography sx={{ fontSize: '13.5px', lineHeight: 1.65, color: '#48606d' }}>
                    {n.excerpt && n.excerpt.length > 140 ? `${n.excerpt.slice(0, 139)}…` : n.excerpt}
                  </Typography>
                </Box>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
        <SectionLink to="/news">Tutte le news</SectionLink>
      </Container>
    </Section>
  );
}
